// Booking questions at checkout (Fijnland Outrides, 26 Sept): questions asked
// for EACH guest, questions asked ONCE per booking (an emergency contact, the
// group's favourite kind of ride) and the statements the guest ticks before
// paying.
//
// "A booking" here is the VISIT: every hold of one service at one start in
// this cart (the server's visit_key). Two options booked together answer the
// once-per-booking questions once and tick the statements once; guests are
// numbered across the visit ("Rider 1 … Rider 4"). A visit of legacy forms
// only (text/number/select per guest, nothing else) keeps today's numbering:
// each hold counts from "Guest 1".
//
// The answer RULES live in @vertu/shared (booking-questions.ts), the same ones
// the server enforces, so the widget, the pay page and the database cannot
// disagree about what is required. This module only adds what the checkout
// needs around them: grouping by visit, the draft a guest is typing, the
// multi-select toggles and the order of the save calls. Pure: no React, no
// network. state/participant-details.ts is kept byte-identical for its pinned
// spec; nothing new goes there.
//
// Drafts live in memory only — never localStorage — because an answer can be
// special personal information (a medical condition, a minor's guardian).

import {
  CLIENT_ROW_KEY,
  SELF_KEY,
  PARTICIPANTS_CHANGED_NOTICE,
  adoptParticipantIds,
  bookerParticipantIdOf,
  checkoutQuestions,
  describeParticipantRefusal,
  describeQuestionProblem,
  formProblems,
  groupPayload,
  isEmptyAnswer,
  isFieldVisible,
  isParticipantIdRefusal,
  missingBeyondParty,
  ridersBeyondPartyMessage,
  numbersOfIds,
  questionsPayload,
  rowKeyOf,
  sameParticipants,
  sentRowKeys,
  withPlaceholderIds,
  riderPrefillTargets,
  prefilledBooker,
  riderSaved,
  savedSensitiveKeys,
  storedRowsCounted,
  startPrefill,
  stopPrefill,
  syncPrefill,
  withoutSavedProblems,
  changeNameBack,
  confirmSamePerson,
  notComingFirst,
  notComingOf,
  participantIdOf,
  renameNeedsAnswer,
  replaceRow,
  replacedIdOf,
  riderNameOf,
  undoReplace,
  withTombstones,
  type AnswerRow,
  type BookerContact,
  type CheckoutConfirmations,
  type FormProblem,
  type GuestNoun,
  type PrefillCopies,
  type PrefillTarget,
  type QuestionField,
} from "@vertu/shared";

/** What a guest is typing. A number field keeps its raw text until it is read. */
export type DraftValue = string | number | boolean | string[] | null | undefined;
/**
 * One person's draft. Beside the answers it may carry two keys that are not
 * answers (participant-ids.ts in @vertu/shared):
 *   _pid  the person's id — only ever one the SERVER gave (a reader's row,
 *         or a save's participant_ids adopted by _key). Never made up here,
 *         so until the server issues ids no row has one and nothing sent
 *         changes. Every rebuild of a row (resizeRows, typedRow, pruneHidden,
 *         applyAnswer, mergeDraft, the prefill) keeps it.
 *   _key  this draft row's own key, client only: never sent. Given to each
 *         row a save sends (withRowKeys) so the ids the reply returns land on
 *         the rows that were sent, never on whatever sits at that index now.
 */
export type DraftRow = Record<string, DraftValue> & { _pid?: string; _key?: string };

export const DEFAULT_NOUN: GuestNoun = { one: "guest", other: "guests" };

/**
 * One form as the checkout uses it: get_cart_booking_participants /
 * get_payment_link_participants, with every key the older payload lacks
 * filled in, so a legacy form (fields only) reads exactly as before.
 */
export type CheckoutForm = {
  booking_id: string;
  service_id: string;
  service_name: string;
  option_name: string | null;
  party_size: number;
  visit_key: string;
  guest_noun: GuestNoun;
  fields: QuestionField[];
  participants: AnswerRow[];
  group_fields: QuestionField[];
  group_answers: AnswerRow;
  confirmations: CheckoutConfirmations | null;
  confirmations_accepted: boolean;
  /**
   * What the SERVER says of the answers it holds (_booking_form_state, the
   * rule the payment gate uses). null: an older payload that does not say.
   * false forces a re-save even when this copy of the rules is satisfied, so
   * the server re-judges the answers instead of the guest looping between a
   * refused payment and a form with nothing to fix.
   */
  complete: boolean | null;
  group_complete: boolean | null;
  /**
   * The reader withheld the STORED answers to this list's sensitive
   * questions (sensitive_withheld). Derived from the question DEFINITIONS,
   * never the answers.
   */
  withheld_rider: boolean;
  withheld_group: boolean;
  /**
   * Who the reader took the caller for (sensitive_access, 20260926140010 —
   * the writers' own rule):
   *   'guest'   the private questions stay on the form and the guest answers
   *             them; once the server reads the form complete (or, while
   *             riders are saved one at a time, for each rider already in),
   *             a withheld answer counts as kept (savedRowKeys) and a save
   *             that leaves it out keeps it (by row position).
   *   'manager' manager and up: the reader returned the real answers, so
   *             nothing is withheld and the form is saved as shown.
   *   'staff'   a signed-in member below manager: the private questions are
   *             shown but cannot be answered (locked_fields), are never sent,
   *             and are not asked here; the server refuses a staff write that
   *             carries one.
   * An older reader that does not say is taken as 'guest'.
   */
  sensitive_access: SensitiveAccess;
  /** 'staff' only: the whole per-guest list, to show the private questions locked in place. */
  locked_fields: QuestionField[];
  /** 'staff' only: the whole once-per-booking list (see locked_fields). */
  locked_group_fields: QuestionField[];
  /** The booker's own person on this form (guest profiles): present only
   *  once the server says, from the reader or a save's reply. Carried, not
   *  yet used. */
  booker_participant_id?: string | null;
};

export type SensitiveAccess = "guest" | "staff" | "manager";

function isObject(v: unknown): v is Record<string, unknown> {
  return !!v && typeof v === "object" && !Array.isArray(v);
}

function nounOf(v: unknown): GuestNoun {
  if (isObject(v)) {
    const { one, other } = v;
    if (typeof one === "string" && one.trim() && typeof other === "string" && other.trim())
      return { one: one.trim(), other: other.trim() };
  }
  return DEFAULT_NOUN;
}

function confirmationsOf(v: unknown): CheckoutConfirmations | null {
  if (!isObject(v) || typeof v.version_id !== "string") return null;
  const statements = Array.isArray(v.statements)
    ? v.statements.filter((s): s is string => typeof s === "string" && s.trim() !== "")
    : [];
  const t = isObject(v.terms) ? v.terms : null;
  const str = (x: unknown) => (typeof x === "string" && x.trim() ? x : null);
  const terms = t && (str(t.title) || str(t.text) || str(t.url))
    ? { title: str(t.title), text: str(t.text), url: str(t.url) }
    : null;
  // An empty version means "none": nothing to tick, nothing to accept.
  if (statements.length === 0 && !terms) return null;
  return {
    version_id: v.version_id,
    version: typeof v.version === "number" ? v.version : 0,
    hash: typeof v.hash === "string" ? v.hash : "",
    statements,
    terms,
  };
}

/** Whether the reader withheld stored answers to this list: it has a sensitive question. */
function withholds(fields: QuestionField[], withheld: boolean): boolean {
  return withheld && fields.some((f) => f.sensitive === true);
}

function accessOf(v: unknown): SensitiveAccess {
  return v === "staff" || v === "manager" ? v : "guest";
}

/** What a staff-rank caller may answer of a list: the private questions taken out. */
function answerable(
  fields: QuestionField[],
  access: SensitiveAccess,
): { fields: QuestionField[]; locked: QuestionField[] } {
  if (access !== "staff" || !fields.some((f) => f.sensitive === true)) return { fields, locked: [] };
  return { fields: fields.filter((f) => f.sensitive !== true), locked: fields };
}

/** A form row from either reader, or null when it is not one. */
export function normaliseForm(raw: unknown): CheckoutForm | null {
  if (!isObject(raw) || typeof raw.booking_id !== "string") return null;
  const party = typeof raw.party_size === "number" && raw.party_size > 0 ? Math.floor(raw.party_size) : 1;
  const withheld = raw.sensitive_withheld === true;
  const access = accessOf(raw.sensitive_access);
  // Below manager, an optional question the service no longer asks (the
  // reader marks it retired) is not asked: its stored answer is kept by a
  // save that leaves it out. A manager's save is stored as sent, so a
  // manager keeps every question (checkoutQuestions).
  const asked = (v: unknown) => {
    const list = Array.isArray(v) ? (v as QuestionField[]) : [];
    return access === "manager" ? list : checkoutQuestions(list);
  };
  const allFields = asked(raw.fields);
  const allGroupFields = asked(raw.group_fields);
  const rider = answerable(allFields, access);
  const group = answerable(allGroupFields, access);
  const fields = rider.fields;
  const groupFields = group.fields;
  const flag = (v: unknown) => (typeof v === "boolean" ? v : null);
  const booker = bookerParticipantIdOf(raw);
  return {
    booking_id: raw.booking_id,
    service_id: typeof raw.service_id === "string" ? raw.service_id : "",
    service_name: typeof raw.service_name === "string" ? raw.service_name : "",
    option_name: typeof raw.option_name === "string" ? raw.option_name : null,
    party_size: party,
    visit_key: typeof raw.visit_key === "string" && raw.visit_key ? raw.visit_key : raw.booking_id,
    guest_noun: nounOf(raw.guest_noun),
    fields,
    participants: Array.isArray(raw.participants) ? (raw.participants as AnswerRow[]) : [],
    group_fields: groupFields,
    group_answers: isObject(raw.group_answers) ? (raw.group_answers as AnswerRow) : {},
    confirmations: confirmationsOf(raw.confirmations),
    confirmations_accepted: raw.confirmations_accepted === true,
    complete: flag(raw.complete),
    group_complete: flag(raw.group_complete),
    withheld_rider: withholds(allFields, withheld),
    withheld_group: withholds(allGroupFields, withheld),
    sensitive_access: access,
    locked_fields: rider.locked,
    locked_group_fields: group.locked,
    ...(booker ? { booker_participant_id: booker } : {}),
  };
}

const LEGACY_TYPES = new Set(["text", "number", "select"]);

/**
 * The legacy shape: {key,label,type:text|number|select,required,options} per
 * guest and nothing else — no once-per-booking questions, no statements, no
 * help, condition, sensitive flag, format or range. Such a form reads exactly
 * as it did before booking questions existed (the LEGACY GUARANTEE).
 */
export function isLegacyForm(form: CheckoutForm): boolean {
  return (
    form.group_fields.length === 0 &&
    form.confirmations === null &&
    !form.withheld_rider &&
    !form.withheld_group &&
    form.fields.every(
      (f) =>
        LEGACY_TYPES.has(f.type) &&
        !f.show_when &&
        // A question the service no longer asks is not a private one (the
        // server marks it sensitive only because its answer is withheld).
        (f.sensitive !== true || f.retired === true) &&
        !f.help &&
        !(f.exclusive_options && f.exclusive_options.length) &&
        !f.format &&
        f.min == null &&
        f.max == null,
    )
  );
}

/** Keep each guest's answers when a hold's headcount changes; new guests start empty. */
export function resizeRows(rows: readonly DraftRow[], count: number): DraftRow[] {
  return Array.from({ length: Math.max(0, count) }, (_, i) => ({ ...(rows[i] ?? {}) }));
}

/** The draft as the rules read it: a number field's text becomes its number. */
export function typedRow(fields: readonly QuestionField[], row: DraftRow | undefined): AnswerRow {
  const out: AnswerRow = { ...(row ?? {}) } as AnswerRow;
  for (const f of fields) {
    if (f.type !== "number") continue;
    const v = row?.[f.key];
    if (typeof v !== "string") continue;
    const t = v.trim();
    if (t === "") {
      delete out[f.key];
      continue;
    }
    const n = Number(t);
    // Unreadable text stays text, so the rules call it "not a number".
    out[f.key] = Number.isFinite(n) ? n : t;
  }
  return out;
}

/**
 * Drop the answers of questions the row now hides. Changing "anything we
 * should know?" to No, or an age to 18, removes the medical details or the
 * guardian's name from the draft itself, not only from what is sent.
 */
export function pruneHidden(fields: readonly QuestionField[], row: DraftRow): DraftRow {
  const typed = typedRow(fields, row);
  let next: DraftRow | null = null;
  for (const f of fields) {
    if (!f.show_when || isFieldVisible(f, typed)) continue;
    const v = row[f.key];
    if (v == null || v === "" || v === false || (Array.isArray(v) && v.length === 0)) continue;
    next ??= { ...row };
    delete next[f.key];
  }
  return next ?? row;
}

/** One draft edit: set the answer, then drop whatever it hides. */
export function applyAnswer(
  fields: readonly QuestionField[],
  row: DraftRow,
  key: string,
  value: DraftValue,
): DraftRow {
  const next: DraftRow = { ...row };
  if (value == null || (Array.isArray(value) && value.length === 0)) delete next[key];
  else next[key] = value;
  return pruneHidden(fields, next);
}

/**
 * A multi-select tick. An exclusive option ("I am not currently confident
 * with any of the above") clears every other tick, and any other tick clears
 * it. The list keeps the options' own order.
 */
export function toggleChoice(
  field: Pick<QuestionField, "options" | "exclusive_options">,
  current: DraftValue,
  option: string,
  checked: boolean,
): string[] {
  const list = Array.isArray(current) ? current.filter((s) => typeof s === "string") : [];
  if (!checked) return list.filter((o) => o !== option);
  const exclusive = new Set(field.exclusive_options ?? []);
  if (exclusive.has(option)) return [option];
  const chosen = new Set([...list.filter((o) => !exclusive.has(o)), option]);
  const order = field.options ?? [];
  return [
    ...order.filter((o) => chosen.has(o)),
    ...[...chosen].filter((o) => !order.includes(o)),
  ];
}

export type VisitConfirmations = {
  /** tickKey(visit, version): where this block's ticks are kept. */
  key: string;
  versionId: string;
  /** A booking of the visit that carries this version (the accept call's target). */
  bookingId: string;
  confirmations: CheckoutConfirmations;
  /** Every form of the visit carrying this version is already accepted. */
  accepted: boolean;
};

export type CheckoutVisit<F extends CheckoutForm = CheckoutForm> = {
  key: string;
  noun: GuestNoun;
  forms: F[];
  /**
   * The first guest number of each form (forms without per-guest questions
   * count none). Guests are numbered across the visit ("Rider 3" is the
   * second option's first rider) — except in a visit of legacy forms only,
   * where each form counts from "Guest 1" exactly as it always has.
   */
  riderStart: number[];
  /** The form whose group questions the visit answers (null: none asked). */
  groupForm: F | null;
  confirmations: VisitConfirmations[];
};

export function tickKey(visitKey: string, versionId: string): string {
  return `${visitKey}|${versionId}`;
}

/** Forms grouped by visit, in the order the server listed them. */
export function groupVisits<F extends CheckoutForm>(forms: readonly F[]): CheckoutVisit<F>[] {
  const visits: CheckoutVisit<F>[] = [];
  for (const form of forms) {
    let visit = visits.find((v) => v.key === form.visit_key);
    if (!visit) {
      visit = { key: form.visit_key, noun: form.guest_noun, forms: [], riderStart: [], groupForm: null, confirmations: [] };
      visits.push(visit);
    }
    const start = visit.forms.reduce((n, f) => n + (f.fields.length > 0 ? f.party_size : 0), 0);
    visit.forms.push(form);
    visit.riderStart.push(start);
    if (!visit.groupForm && form.group_fields.length > 0) visit.groupForm = form;
    const c = form.confirmations;
    if (c) {
      const existing = visit.confirmations.find((x) => x.versionId === c.version_id);
      if (existing) existing.accepted = existing.accepted && form.confirmations_accepted;
      else
        visit.confirmations.push({
          key: tickKey(visit.key, c.version_id),
          versionId: c.version_id,
          bookingId: form.booking_id,
          confirmations: c,
          accepted: form.confirmations_accepted,
        });
    }
  }
  for (const visit of visits)
    if (visit.forms.every(isLegacyForm)) visit.riderStart = visit.forms.map(() => 0);
  return visits;
}

/** Everything the guest has typed or ticked, keyed so a reload can keep it. */
export type CheckoutDraft = {
  /** booking_id → the guests' rows. */
  rows: Record<string, DraftRow[]>;
  /** booking_ids whose rows were edited since they were last saved. */
  dirtyRows: Record<string, true>;
  /** visit key → the once-per-booking answers. */
  group: Record<string, DraftRow>;
  dirtyGroup: Record<string, true>;
  /** tickKey → one boolean per statement. */
  ticks: Record<string, boolean[]>;
  /** "Use my details for Rider 1" is ticked: what was copied, into which hold's first rider. */
  prefill?: DraftPrefill | null;
  /**
   * Kept OPTIONAL private answers the guest chose to clear (clearedKey):
   * no longer kept, and sent as an explicit null so the server drops them.
   */
  cleared?: Record<string, true>;
  /**
   * Set by mergeDraft only: the holds whose edited rows it did NOT keep,
   * because the people the server holds are no longer the ones those rows
   * were for (their ids differ). The page says PARTICIPANTS_CHANGED_NOTICE.
   * Absent when there are none (always, while the server issues no ids).
   */
  changedElsewhere?: string[];
  /**
   * The full-page checkout (state/rider-autosave.ts): riders this page put
   * on their cards FROM THE SERVER after a conflict (a save refused
   * rider_changed, or a read that found the rider changed under the guest's
   * typing), keyed adoptedKey(), each with its answers as adopted
   * (everything but the private ones). While the card still holds those
   * answers it is the stored rider, and that rider's withheld private
   * answers stay kept. Once the guest changes any of them, none counts as
   * kept any more: the private questions are asked again, and a private
   * answer left empty is sent as an explicit null. So typing a different
   * person over an adopted rider never carries the stored person's private
   * answers onto them (the owner's rule: never guess who a rider is).
   */
  adopted?: Record<string, string>;
  /**
   * "Is this still {name}?" (P4a, rider-rename.ts in @vertu/shared): the
   * saved riders (by id) a save stopped to ask about. "ask": the question;
   * "booker": the booker answered Yes, which only the venue may say (change
   * it back, or replace the rider). Shown while the card still changes that
   * rider's name; gone once answered. Page memory only.
   */
  renameAsked?: Record<string, "ask" | "booker">;
  /**
   * The riders among renameAsked whose question the SERVER opened
   * (identity_changed where this page's own check saw no change: a name it
   * cannot read). Shown until answered, whatever the card shows.
   */
  renameForced?: Record<string, true>;
};

/** Where a cleared answer is noted: a guest's row of a hold, or a visit's once-per-booking answers. */
export function clearedKey(
  target: { bookingId: string; row: number } | { visitKey: string },
  key: string,
): string {
  return "visitKey" in target ? `g|${target.visitKey}|${key}` : `r|${target.bookingId}|${target.row}|${key}`;
}

export type DraftPrefill = { bookingId: string; copied: PrefillCopies };

export const EMPTY_DRAFT: CheckoutDraft = { rows: {}, dirtyRows: {}, group: {}, dirtyGroup: {}, ticks: {} };

/**
 * The draft for freshly loaded forms: an edited form or visit keeps what the
 * guest typed (resized to the hold's new headcount); everything else starts
 * from what the server holds. A visit whose earliest hold went away is
 * matched by any booking it still shares. Ticks survive while the version is
 * the same; an accepted version starts ticked.
 *
 * Rider numbers: a hold's edited rows are kept only while each row's id is
 * the id of the stored row at the same place (no id on either side counts
 * as the same: a new person, or a server that issues none, which keeps
 * today's behaviour exactly; a row without an id over a stored placeholder,
 * a rider not in yet, is the same too, and takes that placeholder's id).
 * Otherwise someone else changed who is on the booking, so the hold shows
 * what the server holds now and the draft says so (changedElsewhere).
 */
export function mergeDraft(
  previous: CheckoutDraft,
  previousVisits: readonly CheckoutVisit[],
  visits: readonly CheckoutVisit[],
): CheckoutDraft {
  const next: CheckoutDraft = { rows: {}, dirtyRows: {}, group: {}, dirtyGroup: {}, ticks: {} };
  for (const visit of visits) {
    for (const form of visit.forms) {
      const count = form.fields.length > 0 ? form.party_size : 0;
      let kept = previous.dirtyRows[form.booking_id] ? previous.rows[form.booking_id] : undefined;
      if (kept && !sameParticipants(resizeRows(kept, count), form.participants)) {
        kept = undefined;
        (next.changedElsewhere ??= []).push(form.booking_id);
      }
      if (kept) next.dirtyRows[form.booking_id] = true;
      // A kept row over a slot nobody is in yet takes that slot's id
      // (withPlaceholderIds), so a save of the whole list names it.
      next.rows[form.booking_id] = kept
        ? withPlaceholderIds(resizeRows(kept, count), form.participants)
        : resizeRows(form.participants as DraftRow[], count);
    }
    const before = previousVisits.find(
      (p) => p.key === visit.key || p.forms.some((f) => visit.forms.some((g) => g.booking_id === f.booking_id)),
    );
    if (before && previous.dirtyGroup[before.key] && previous.group[before.key]) {
      next.group[visit.key] = { ...previous.group[before.key] };
      next.dirtyGroup[visit.key] = true;
    } else {
      next.group[visit.key] = { ...((visit.groupForm?.group_answers ?? {}) as DraftRow) };
    }
    for (const c of visit.confirmations) {
      const kept = before ? previous.ticks[tickKey(before.key, c.versionId)] : undefined;
      const n = c.confirmations.statements.length;
      next.ticks[c.key] = c.accepted
        ? Array.from({ length: n }, (_, i) => kept?.[i] ?? true)
        : Array.from({ length: n }, (_, i) => kept?.[i] ?? false);
    }
  }
  // "Use my details" stays ticked while Rider 1 is still the same hold's and
  // nothing is saved for Rider 1 yet (riderOne). A tick these forms no longer
  // offer is taken back (withdrawPrefill).
  const prefill = previous.prefill;
  let resetRider: string | null = null;
  if (prefill) {
    if (prefillStillOffered(previous, visits)) next.prefill = prefill;
    else resetRider = withdrawPrefill(next, prefill, previousVisits, visits);
  }
  // A cleared answer stays cleared while the edit it belongs to is kept.
  for (const key of Object.keys(previous.cleared ?? {})) {
    const [scope, id = ""] = key.split("|");
    const rest = key.slice(scope!.length + id.length + 2);
    if (scope === "r" && next.dirtyRows[id]) (next.cleared ??= {})[key] = true;
    if (scope === "g") {
      const before = previousVisits.find((p) => p.key === id);
      const visit = visits.find(
        (v) => v.key === id || !!before?.forms.some((f) => v.forms.some((g) => g.booking_id === f.booking_id)),
      );
      if (visit && next.dirtyGroup[visit.key]) (next.cleared ??= {})[clearedKey({ visitKey: visit.key }, rest)] = true;
    }
  }
  // Rider 1 now shows the saved rider: nothing of the draft's is cleared there.
  if (resetRider && next.cleared)
    for (const key of Object.keys(next.cleared))
      if (key.startsWith(`r|${resetRider}|0|`)) delete next.cleared[key];
  // An adopted rider stays adopted while the edit it belongs to is kept.
  for (const [key, mark] of Object.entries(previous.adopted ?? {})) {
    const id = key.slice(0, key.lastIndexOf("|"));
    if (next.dirtyRows[id] && key !== adoptedKey(resetRider ?? "", 0)) (next.adopted ??= {})[key] = mark;
  }
  // A question asked about a saved rider stays asked (it names the rider by id).
  if (previous.renameAsked && Object.keys(previous.renameAsked).length) next.renameAsked = { ...previous.renameAsked };
  if (previous.renameForced && Object.keys(previous.renameForced).length) next.renameForced = { ...previous.renameForced };
  return next;
}

// ─── Riders adopted from the server (the full-page checkout) ────────────────

/** Where an adopted rider is noted (CheckoutDraft.adopted). */
export function adoptedKey(bookingId: string, ordinal: number): string {
  return `${bookingId}|${ordinal}`;
}

/** Who a rider row is, as far as the page can tell: its answers to the questions that are not private, as a stable string. */
function identityKey(fields: readonly QuestionField[], row: DraftRow | undefined): string {
  const open = fields.filter((f) => f.sensitive !== true);
  const payload = questionsPayload(open, [typedRow(open, row)])[0] ?? {};
  return JSON.stringify(
    Object.keys(payload)
      .filter((k) => !k.startsWith("_"))
      .sort()
      .map((k) => [k, payload[k]]),
  );
}

/** Note that this rider's card now shows the server's rider (see CheckoutDraft.adopted). */
export function markAdopted(draft: CheckoutDraft, form: CheckoutForm, ordinal: number): CheckoutDraft {
  const key = adoptedKey(form.booking_id, ordinal);
  const mark = identityKey(form.fields, draft.rows[form.booking_id]?.[ordinal]);
  return { ...draft, adopted: { ...(draft.adopted ?? {}), [key]: mark } };
}

/** The draft with this rider no longer adopted (a save of it was accepted: the server holds what the guest gave). */
export function withoutAdopted(draft: CheckoutDraft, bookingId: string, ordinal: number): CheckoutDraft {
  const key = adoptedKey(bookingId, ordinal);
  if (!draft.adopted || !(key in draft.adopted)) return draft;
  const adopted = { ...draft.adopted };
  delete adopted[key];
  return { ...draft, adopted };
}

/**
 * Is this an adopted rider (CheckoutDraft.adopted) the guest has since
 * changed? Then it may be someone else now, and none of the stored rider's
 * private answers counts as theirs.
 */
export function adoptedAndChanged(form: CheckoutForm, draft: CheckoutDraft | undefined, ordinal: number): boolean {
  const mark = draft?.adopted?.[adoptedKey(form.booking_id, ordinal)];
  return mark !== undefined && mark !== identityKey(form.fields, draft!.rows[form.booking_id]?.[ordinal]);
}

/** A form of these visits, by its booking. */
function formIn<F extends CheckoutForm>(visits: readonly CheckoutVisit<F>[], bookingId: string): F | undefined {
  for (const visit of visits) for (const form of visit.forms) if (form.booking_id === bookingId) return form;
  return undefined;
}

function blankValue(v: DraftValue | unknown): boolean {
  return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
}

/** Two draft rows hold the same answers (an empty answer and a missing one
 *  are the same; the client-only _key is not an answer). */
function sameRow(a: DraftRow | undefined, b: DraftRow | undefined): boolean {
  const x = a ?? {};
  const y = b ?? {};
  for (const key of new Set([...Object.keys(x), ...Object.keys(y)])) {
    if (key === CLIENT_ROW_KEY) continue;
    if (blankValue(x[key]) && blankValue(y[key])) continue;
    if (JSON.stringify(x[key]) !== JSON.stringify(y[key])) return false;
  }
  return true;
}

/**
 * mergeDraft, for a "use my details" tick the fresh forms no longer offer.
 * Rider 1 SAVED meanwhile (another tab, or the venue, saved it after this
 * page last read the forms): Rider 1 shows what the server holds now, so the
 * booker's details (and whatever was typed into Rider 1 around the tick) are
 * never saved over that rider; when the tick was the hold's only edit (every
 * other row still as last read) the hold is not edited any more and shows
 * the server's rows. Otherwise (the box went for another reason) the copy is
 * taken back as unticking does. Returns the booking whose Rider 1 now shows
 * the saved rider, or null. A plain rule on what is stored, not a guess at
 * who a rider is.
 */
function withdrawPrefill(
  next: CheckoutDraft,
  prefill: DraftPrefill,
  previousVisits: readonly CheckoutVisit[],
  visits: readonly CheckoutVisit[],
): string | null {
  const id = prefill.bookingId;
  const rows = next.rows[id];
  const now = formIn(visits, id);
  if (!rows?.length || !now || !next.dirtyRows[id]) return null;
  if (!riderSaved(now.participants[0], now.withheld_rider)) {
    rows[0] = stopPrefill(rows[0]!, prefill.copied);
    return null;
  }
  const stored = resizeRows(now.participants as DraftRow[], rows.length);
  const before = formIn(previousVisits, id);
  const lastRead = resizeRows((before?.participants ?? []) as DraftRow[], rows.length);
  if (before && rows.every((row, i) => i === 0 || sameRow(row, lastRead[i]))) {
    next.rows[id] = stored;
    delete next.dirtyRows[id];
  } else {
    rows[0] = stored[0]!;
  }
  return id;
}

/**
 * Is "use my details" still offered for the hold it was ticked on? Always
 * true while it is not ticked. The page reads the forms again right before a
 * save that carries the tick: false means Rider 1 was saved meanwhile, and
 * the page merges the fresh forms (mergeDraft takes the tick back) instead of
 * saving (prefillWithdrawnMessage).
 */
export function prefillStillOffered(draft: CheckoutDraft, visits: readonly CheckoutVisit[]): boolean {
  return !draft.prefill || riderOne(visits)?.form.booking_id === draft.prefill.bookingId;
}

/** Said when a save found Rider 1 saved meanwhile (prefillStillOffered false). */
export function prefillWithdrawnMessage(noun: GuestNoun = DEFAULT_NOUN): string {
  const rider = `${nounTitle(noun)} 1`;
  return `${rider} has just been saved elsewhere, so your details were not used for ${rider}. Please check the details and save again.`;
}

// ─── Saved sensitive answers ────────────────────────────────────────────────

/** Per stored row, what the server may hold for this guest (the draft aside). */
function storedRowKeys(form: CheckoutForm): Set<string>[] {
  if (form.sensitive_access !== "guest" || !form.withheld_rider) return [];
  return storedRowsCounted(form.participants, form.party_size, form.complete).map((counted, i) =>
    counted ? savedSensitiveKeys(form.fields, typedRow(form.fields, form.participants[i] as DraftRow)) : new Set<string>(),
  );
}

function storedGroupKeys(form: CheckoutForm | null | undefined): Set<string> {
  if (!form || form.sensitive_access !== "guest" || !form.withheld_group || form.group_complete !== true)
    return new Set();
  return savedSensitiveKeys(form.group_fields, typedRow(form.group_fields, form.group_answers as DraftRow));
}

/**
 * Per stored guest row: the sensitive questions whose answers the server
 * holds but did not return (see savedSensitiveKeys in @vertu/shared), so the
 * guest need not answer them again. Only for a guest's withheld form whose
 * stored rows line up with the party, and only for the rows storedRowsCounted
 * counts: all of them once the server reads the form complete; while riders
 * are saved one at a time, each rider already in. With the draft: none the
 * guest cleared.
 */
export function savedRowKeys(form: CheckoutForm, draft?: CheckoutDraft): Set<string>[] {
  return storedRowKeys(form).map((keys, i) => {
    // An adopted rider the guest changed keeps nothing (CheckoutDraft.adopted).
    if (adoptedAndChanged(form, draft, i)) return new Set<string>();
    // A new rider typed in place of a saved one (Replace) keeps nothing of theirs.
    if (replacedIdOf(draft?.rows[form.booking_id]?.[i]) !== null) return new Set<string>();
    const out = new Set(keys);
    for (const key of keys)
      if (draft?.cleared?.[clearedKey({ bookingId: form.booking_id, row: i }, key)]) out.delete(key);
    return out;
  });
}

/** The once-per-booking questions' saved sensitive answers (see savedRowKeys). */
export function savedGroupKeys(form: CheckoutForm | null | undefined, draft?: CheckoutDraft): Set<string> {
  const keys = storedGroupKeys(form);
  if (!form || keys.size === 0) return keys;
  for (const key of [...keys])
    if (draft?.cleared?.[clearedKey({ visitKey: form.visit_key }, key)]) keys.delete(key);
  return keys;
}

/** Does any saved answer of this form stand in for an empty box right now? */
export function formUsesSaved(form: CheckoutForm, draft?: CheckoutDraft): boolean {
  return savedRowKeys(form, draft).some((keys) => keys.size > 0) || savedGroupKeys(form, draft).size > 0;
}

/**
 * Clear a kept OPTIONAL private answer (on: true), or take the clearing back.
 * A cleared answer is no longer kept: its box is an ordinary empty optional
 * question, and the save sends it as an explicit null so the server drops it.
 */
export function setCleared(
  draft: CheckoutDraft,
  target: { bookingId: string; row: number } | { visitKey: string },
  key: string,
  on: boolean,
): CheckoutDraft {
  const k = clearedKey(target, key);
  if (!!draft.cleared?.[k] === on) return draft;
  const cleared = { ...(draft.cleared ?? {}) };
  if (on) cleared[k] = true;
  else delete cleared[k];
  return "visitKey" in target
    ? { ...draft, cleared, dirtyGroup: { ...draft.dirtyGroup, [target.visitKey]: true } }
    : { ...draft, cleared, dirtyRows: { ...draft.dirtyRows, [target.bookingId]: true } };
}

function riderProblems(form: CheckoutForm, rows: AnswerRow[], draft?: CheckoutDraft): FormProblem[] {
  return withoutSavedProblems(formProblems(form.fields, rows, form.party_size), savedRowKeys(form, draft));
}

function groupProblems(form: CheckoutForm, answers: AnswerRow, draft?: CheckoutDraft): FormProblem[] {
  return withoutSavedProblems(formProblems(form.group_fields, [answers], 1), [savedGroupKeys(form, draft)]);
}

/**
 * The private answers a save must send as an explicit null: a kept answer
 * the guest cleared (Clear). Never a hidden question's, and never over an
 * answer the guest typed.
 */
function explicitNulls(
  fields: readonly QuestionField[],
  stored: Set<string> | undefined,
  isCleared: (key: string) => boolean,
  payload: AnswerRow,
  typed: AnswerRow,
): AnswerRow {
  if (!stored || stored.size === 0) return payload;
  let out = payload;
  for (const f of fields) {
    if (!stored.has(f.key) || !isCleared(f.key)) continue;
    if (!isFieldVisible(f, typed) || !isEmptyAnswer(f, typed[f.key]) || f.key in out) continue;
    if (out === payload) out = { ...payload };
    out[f.key] = null;
  }
  return out;
}

// ─── "Use my details for Rider 1" ───────────────────────────────────────────

/**
 * Rider 1 of the checkout: the first guest of the first form that asks
 * per-guest questions, with the questions the booker's details go into
 * (their keys are the preset's: riderPrefillTargets). null — no box is
 * shown — when:
 *   * Rider 1 already has something saved on the server (riderSaved): the
 *     box fills in a rider nobody has saved yet and never turns a saved
 *     rider into someone else, so it never has to decide who a rider is.
 *     "Saved" is judged on the forms as last read, so a page that shows the
 *     box reads them again before a save that carries the tick
 *     (prefillStillOffered), and a reload that finds Rider 1 saved meanwhile
 *     takes the tick back (mergeDraft). The server cannot tell (rows carry
 *     no rider id yet), so the moment between that read and the save stays
 *     open until the participant-ID design;
 *   * the caller is a member below manager and the form asks private
 *     questions: they could not answer those for the booker anyway;
 *   * the form asks none of the preset's name, email or mobile questions.
 */
export function riderOne<F extends CheckoutForm>(
  visits: readonly CheckoutVisit<F>[],
): { visit: CheckoutVisit<F>; form: F; number: number; targets: PrefillTarget[] } | null {
  for (const visit of visits)
    for (let i = 0; i < visit.forms.length; i++) {
      const form = visit.forms[i]!;
      if (form.fields.length === 0) continue;
      if (riderSaved(form.participants[0], form.withheld_rider)) return null;
      if (form.sensitive_access === "staff" && form.locked_fields.length > 0) return null;
      const targets = riderPrefillTargets(form.fields);
      return targets.length ? { visit, form, number: visit.riderStart[i]! + 1, targets } : null;
    }
  return null;
}

function withRiderOne(draft: CheckoutDraft, bookingId: string, row: DraftRow): CheckoutDraft {
  const rows = [...(draft.rows[bookingId] ?? [])];
  rows[0] = row;
  return {
    ...draft,
    rows: { ...draft.rows, [bookingId]: rows },
    dirtyRows: { ...draft.dirtyRows, [bookingId]: true },
  };
}

/**
 * Tick or untick "use my details for Rider 1". Ticking copies the booker's
 * details into Rider 1's preset questions; unticking takes back only what is
 * still as copied (a question changed by hand keeps its answer), so what was
 * typed before the tick comes back. Offered only while Rider 1 has nothing
 * saved (riderOne), so the rider it fills is nobody's yet.
 */
export function setRiderOnePrefill(
  draft: CheckoutDraft,
  visits: readonly CheckoutVisit[],
  booker: BookerContact,
  on: boolean,
): CheckoutDraft {
  if (!on) {
    const prefill = draft.prefill;
    if (!prefill) return draft;
    const row = draft.rows[prefill.bookingId]?.[0];
    const next = row ? stopPrefill(row, prefill.copied) : row;
    const out = next && next !== row ? withRiderOne(draft, prefill.bookingId, next) : { ...draft };
    out.prefill = null;
    return out;
  }
  const one = riderOne(visits);
  if (!one || draft.prefill?.bookingId === one.form.booking_id) return draft;
  const id = one.form.booking_id;
  const { row, copied } = startPrefill(draft.rows[id]?.[0] ?? {}, one.targets, booker);
  return { ...withRiderOne(draft, id, pruneHidden(one.form.fields, row)), prefill: { bookingId: id, copied } };
}

/** Add the request-only self intent, never a question answer or a saved-row marker. */
function withBookerIntent(
  form: CheckoutForm,
  draft: CheckoutDraft,
  ordinal: number,
  payload: AnswerRow,
): AnswerRow {
  const prefill = draft.prefill;
  if (
    ordinal !== 0 || prefill?.bookingId !== form.booking_id ||
    riderSaved(form.participants[ordinal], form.withheld_rider) ||
    replacedIdOf(draft.rows[form.booking_id]?.[ordinal]) !== null ||
    !prefilledBooker(form.fields, payload, prefill.copied)
  ) return payload;
  return { ...payload, [SELF_KEY]: true };
}

/**
 * The booker edited their details while the box is ticked: the edit flows
 * into the Rider 1 questions the guest has not changed by hand. The same
 * draft when nothing changes.
 */
export function syncRiderOnePrefill(
  draft: CheckoutDraft,
  visits: readonly CheckoutVisit[],
  booker: BookerContact,
): CheckoutDraft {
  const prefill = draft.prefill;
  const one = riderOne(visits);
  if (!prefill || !one || one.form.booking_id !== prefill.bookingId) return draft;
  const row = draft.rows[prefill.bookingId]?.[0] ?? {};
  const next = syncPrefill(row, one.targets, booker, prefill.copied);
  if (next.row === row && next.copied === prefill.copied) return draft;
  const out = next.row === row ? { ...draft } : withRiderOne(draft, prefill.bookingId, next.row);
  out.prefill = { ...prefill, copied: next.copied };
  return out;
}

function label(form: CheckoutForm): string {
  return form.option_name ?? form.service_name;
}

/**
 * The first thing still missing in a visit, worded for the guest, or null.
 * Guests are numbered across the visit. `ticks` false: the answers only (the
 * Save button's check); true: the statements too (Proceed's).
 */
export function visitIssue(
  visit: CheckoutVisit,
  draft: CheckoutDraft,
  withTicks: boolean,
): string | null {
  const noun = visit.noun;
  for (let i = 0; i < visit.forms.length; i++) {
    const form = visit.forms[i]!;
    if (form.fields.length === 0) continue;
    // More riders saved than the booking is now for: nobody is saved until
    // the guest says who is not coming (P4a).
    if (notComingOfForm(form)) return `${label(form)}: ${notComingFirst(noun)}`;
    const rows = (draft.rows[form.booking_id] ?? []).map((r) => typedRow(form.fields, r));
    const problem = riderProblems(form, rows, draft)[0];
    if (!problem) continue;
    if (problem.row < 0) return `${label(form)}: Please enter details for each ${noun.one}.`;
    const field = form.fields.find((f) => f.key === problem.key);
    if (!field) return `${label(form)}: Please check each ${noun.one}'s details.`;
    return `${label(form)}: ${describeQuestionProblem(problem, field, noun, visit.riderStart[i]! + problem.row)}`;
  }
  const g = visit.groupForm;
  if (g) {
    const answers = typedRow(g.group_fields, draft.group[visit.key]);
    const problem = groupProblems(g, answers, draft)[0];
    if (problem) {
      const field = g.group_fields.find((f) => f.key === problem.key);
      return field
        ? describeQuestionProblem(problem, field, noun, null)
        : "Please check the booking details.";
    }
  }
  if (withTicks) {
    for (const c of visit.confirmations) {
      const ticks = draft.ticks[c.key] ?? [];
      if (!c.confirmations.statements.every((_, i) => ticks[i] === true))
        return "Please tick each confirmation before paying.";
    }
  }
  return null;
}

/**
 * The server already holds a complete answer set for this form: its guests
 * and its once-per-booking answers pass the rules as stored, saved (withheld)
 * sensitive answers counted. (The checkout's "Guest details saved." line; an
 * all-optional form nobody filled in reads "These details are optional."
 * instead, as it always has.)
 */
export function storedAnswersValid(form: CheckoutForm): boolean {
  const rows = form.participants.map((r) => typedRow(form.fields, r as DraftRow));
  return (
    (form.fields.length === 0 || riderProblems(form, rows).length === 0) &&
    (form.group_fields.length === 0 ||
      groupProblems(form, typedRow(form.group_fields, form.group_answers as DraftRow)).length === 0)
  );
}

/** Proceed may be pressed: every visit's answers are complete and every statement is ticked. */
export function checkoutReady(visits: readonly CheckoutVisit[], draft: CheckoutDraft): boolean {
  return visits.every((v) => visitIssue(v, draft, true) === null);
}

/** Whether anything in the checkout asks the guest for something. */
export function asksAnything(visits: readonly CheckoutVisit[]): boolean {
  return visits.some(
    (v) =>
      v.confirmations.length > 0 ||
      v.groupForm != null ||
      v.forms.some((f) => f.fields.length > 0),
  );
}

export type CommitPlan = {
  /** save_*_participants, one per form whose rows must be written. */
  rows: { bookingId: string; participants: AnswerRow[] }[];
  /** save_*_group_details, one per visit (the server copies it to the visit's other holds). */
  group: { visitKey: string; bookingId: string; answers: AnswerRow }[];
  /** accept_*_confirmations, one per version the visit has not accepted yet. */
  accept: { visitKey: string; bookingId: string; versionId: string }[];
};

/**
 * The calls Proceed (or Pay) makes, in order: every form's guests, then each
 * visit's once-per-booking answers, then the statements. Only what the server
 * does not already hold: an edited draft, a stored answer set that no longer
 * fits (the headcount changed), or one the SERVER calls incomplete
 * (complete / group_complete false) even though these rules accept it — the
 * re-save makes the server judge it again, and a refusal is shown, instead of
 * a payment refused with nothing on the form to fix. Hidden answers are never
 * sent, and neither is an empty one: a saved sensitive answer left empty is
 * left OUT, so the server keeps it (a blank would overwrite it). The one
 * exception is a kept optional answer the guest cleared: it is sent as an
 * explicit null, so the server drops it.
 */
export function commitPlan(visits: readonly CheckoutVisit[], draft: CheckoutDraft): CommitPlan {
  const plan: CommitPlan = { rows: [], group: [], accept: [] };
  for (const visit of visits) {
    for (const form of visit.forms) {
      if (form.fields.length === 0) continue;
      const stored = form.participants.map((r) => typedRow(form.fields, r as DraftRow));
      // Stored answers that no longer fit (the headcount went down) are
      // rewritten only where something is required: an all-optional form
      // never blocks payment, so there is nothing to put right.
      const stale =
        form.complete === false ||
        (form.fields.some((f) => f.required) && riderProblems(form, stored).length > 0);
      if (!draft.dirtyRows[form.booking_id] && !stale) continue;
      const draftRows = draft.rows[form.booking_id] ?? [];
      const rows = draftRows.map((r) => typedRow(form.fields, r));
      const kept = storedRowKeys(form);
      const payload = questionsPayload(form.fields, rows).map((row, i) =>
        // A new rider in place of a saved one (Replace) sends none of theirs.
        replacedIdOf(draftRows[i]) !== null
          ? row
          : explicitNulls(
              form.fields,
              kept[i],
              (key) => !!draft.cleared?.[clearedKey({ bookingId: form.booking_id, row: i }, key)],
              row,
              rows[i] ?? {},
            ),
      );
      // Replace (P4a): the saved rider each replaced card stands in for goes
      // as a tombstone beside the new rider (who has no id: nothing carried).
      plan.rows.push({
        bookingId: form.booking_id,
        participants: withTombstones(payload.map((row, i) => withBookerIntent(form, draft, i, row)), draftRows),
      });
    }
    const g = visit.groupForm;
    if (g) {
      const stale =
        g.group_complete === false ||
        (g.group_fields.some((f) => f.required) && groupProblems(g, g.group_answers).length > 0);
      if (draft.dirtyGroup[visit.key] || stale) {
        const typed = typedRow(g.group_fields, draft.group[visit.key]);
        plan.group.push({
          visitKey: visit.key,
          bookingId: g.booking_id,
          answers: explicitNulls(
            g.group_fields,
            storedGroupKeys(g),
            (key) => !!draft.cleared?.[clearedKey({ visitKey: visit.key }, key)],
            groupPayload(g.group_fields, typed),
            typed,
          ),
        });
      }
    }
    for (const c of visit.confirmations)
      if (!c.accepted) plan.accept.push({ visitKey: visit.key, bookingId: c.bookingId, versionId: c.versionId });
  }
  return plan;
}

/**
 * ONE rider's row as a save sends it (save_booking_participant_row): exactly
 * the row commitPlan would put at this place in the whole list — the typed
 * answers as questionsPayload builds them (empty and hidden answers left
 * out, so a kept private answer left empty stays kept; the reserved row keys
 * such as _pid passed through; the client-only _key never), with a kept
 * optional private answer the guest cleared sent as an explicit null.
 */
export function riderRowPayload(form: CheckoutForm, draft: CheckoutDraft, ordinal: number): AnswerRow {
  const typed = typedRow(form.fields, draft.rows[form.booking_id]?.[ordinal]);
  const payload = questionsPayload(form.fields, [typed])[0] ?? {};
  // A new rider in place of a saved one (Replace): its own answers only.
  if (replacedIdOf(draft.rows[form.booking_id]?.[ordinal]) !== null) return payload;
  // An adopted rider the guest changed (CheckoutDraft.adopted): every
  // private question on the card left empty goes as an explicit null, so the
  // server carries none of the stored rider's private answers over.
  if (adoptedAndChanged(form, draft, ordinal)) {
    const out: AnswerRow = { ...payload };
    for (const f of form.fields)
      if (f.sensitive === true && !(f.key in out) && isFieldVisible(f, typed) && isEmptyAnswer(f, typed[f.key]))
        out[f.key] = null;
    return out;
  }
  return withBookerIntent(
    form, draft, ordinal,
    explicitNulls(
      form.fields,
      storedRowKeys(form)[ordinal],
      (key) => !!draft.cleared?.[clearedKey({ bookingId: form.booking_id, row: ordinal }, key)],
      payload,
      typed,
    ),
  );
}

/** "Rider", from {one:"rider"}. */
export function nounTitle(noun: GuestNoun): string {
  const one = noun.one;
  return one.charAt(0).toUpperCase() + one.slice(1);
}

/**
 * The saving calls' refusals, worded for the guest. Each refusal keeps its
 * own words and leaves the kept private answers as they are.
 * sensitive_answers_need_manager: a signed-in member below manager sent a
 * private answer, or the change needs one they cannot see.
 */
export function describeSaveRefusal(reason: unknown, noun: GuestNoun = DEFAULT_NOUN): string {
  // The refusals the rider-number server change adds (participant-ids.ts).
  const participant = describeParticipantRefusal(reason, noun);
  if (participant) return participant;
  switch (reason) {
    case "sensitive_answers_need_manager":
      return "Some answers are private: only the guest or a venue manager can fill them in. Please ask the guest to complete this form.";
    case "invalid_participant_details":
      return `Please check each ${noun.one}'s details and try again.`;
    case "invalid_booking_details":
      return "Please check the booking details and try again.";
    case "confirmations_changed":
      return "The booking terms were just updated. Please review and tick them again.";
    case "checkout_confirmations_required":
      return "Please tick each confirmation before paying.";
    case "hold_expired":
      return "Your hold has expired. Please reselect your booking.";
    default:
      return `${nounTitle(noun)} details could not be saved. Please retry; your booking may have changed or expired.`;
  }
}

// ─── Rider numbers (participant ids): carried, never made up ────────────────
//
// Until the server issues ids (the rider-ID spec's P3) none of this changes
// what is sent: no reader row has a _pid, no save returns participant_ids,
// so nothing is adopted and every row goes out exactly as before. The
// client-only _key a sent row is given never leaves the page.

let rowKeySeq = 0;
/** A fresh client-only row key (unique within this page). */
export function newRowKey(): string {
  rowKeySeq += 1;
  return `k${rowKeySeq}`;
}

/**
 * Give every row of these holds a client-only _key, so a save's returned ids
 * can be adopted by it (adoptSavedIds). Called with the rows a save is about
 * to send, right before the plan is made. The same draft when every row
 * already has one.
 */
export function withRowKeys(
  draft: CheckoutDraft,
  bookingIds: readonly string[],
  mint: () => string = newRowKey,
): CheckoutDraft {
  let rows: Record<string, DraftRow[]> | null = null;
  for (const id of bookingIds) {
    const list = draft.rows[id];
    if (!list || list.every((row) => rowKeyOf(row) !== null)) continue;
    rows ??= { ...draft.rows };
    rows[id] = list.map((row) => (rowKeyOf(row) !== null ? row : { ...row, [CLIENT_ROW_KEY]: mint() }));
  }
  return rows ? { ...draft, rows } : draft;
}

/**
 * The keys withRowKeys gave, carried onto the page's CURRENT draft: a row
 * gets the key its own object was given (the same object, unedited since),
 * never by index. A row edited meanwhile gets none, so no id is adopted for
 * it (the next save then fails closed on the server). The keyed draft itself
 * when the current draft is still the one keyed.
 */
export function carryRowKeys(current: CheckoutDraft, before: CheckoutDraft, keyed: CheckoutDraft): CheckoutDraft {
  if (current === before) return keyed;
  let rows: Record<string, DraftRow[]> | null = null;
  for (const [id, list] of Object.entries(current.rows)) {
    const was = before.rows[id];
    const now = keyed.rows[id];
    if (!was || !now || was === now) continue;
    const next = list.map((row) => {
      if (rowKeyOf(row) !== null) return row;
      const at = was.indexOf(row);
      const key = at >= 0 ? rowKeyOf(now[at]) : null;
      return key ? { ...row, [CLIENT_ROW_KEY]: key } : row;
    });
    if (next.some((row, i) => row !== list[i])) (rows ??= { ...current.rows })[id] = next;
  }
  return rows ? { ...current, rows } : current;
}

/** The client-only keys of the rows the plan sends for this hold, in the server's order. */
export function planRowKeys(draft: CheckoutDraft, bookingId: string): (string | null)[] {
  return sentRowKeys(draft.rows[bookingId] ?? []);
}

/**
 * A save of this hold's rows was accepted: the ids the reply returned
 * (participant_ids, in the order sent) are written onto the rows that were
 * sent, found by the _key each had at send time. No ids in the reply
 * (today's server): the same draft.
 */
export function adoptSavedIds(
  draft: CheckoutDraft,
  bookingId: string,
  sentKeys: readonly (string | null)[],
  ids: readonly string[] | null,
): CheckoutDraft {
  const rows = draft.rows[bookingId];
  if (!rows) return draft;
  const next = adoptParticipantIds(rows, sentKeys, ids);
  return next === rows ? draft : { ...draft, rows: { ...draft.rows, [bookingId]: next } };
}

/**
 * Stop keeping this hold's edited rows: the server refused them because the
 * people it holds are not the ones the rows are for. The next read shows
 * what it holds (with PARTICIPANTS_CHANGED_NOTICE). Its cleared marks and a
 * "use my details" tick on it go too.
 */
export function discardRows(draft: CheckoutDraft, bookingId: string): CheckoutDraft {
  const dirtyRows = { ...draft.dirtyRows };
  delete dirtyRows[bookingId];
  const cleared = draft.cleared
    ? (Object.fromEntries(
        Object.entries(draft.cleared).filter(([key]) => !key.startsWith(`r|${bookingId}|`)),
      ) as Record<string, true>)
    : draft.cleared;
  return {
    ...draft,
    dirtyRows,
    ...(cleared ? { cleared } : {}),
    prefill: draft.prefill?.bookingId === bookingId ? null : draft.prefill,
  };
}

/**
 * A refused save of one hold's rows, when the refusal is one the rider-number
 * server change adds (null for every other reason: the page keeps its own
 * words for those). `reload`: the forms must be read again, with this hold's
 * edits dropped (discardRows), because the ids no longer match the people
 * the server holds. People the refusal names (its participant_ids) are
 * numbered as the page numbers them ("Rider 3"), found by id among the rows
 * that were sent — never by position.
 */
export function describeRowsRefusal(
  visits: readonly CheckoutVisit[],
  draft: CheckoutDraft,
  bookingId: string,
  reason: unknown,
  ids: readonly string[] | null,
): { message: string; reload: boolean; ask?: string[] } | null {
  let noun = DEFAULT_NOUN;
  let first = 1;
  let form: CheckoutForm | null = null;
  for (const visit of visits) {
    const i = visit.forms.findIndex((f) => f.booking_id === bookingId);
    if (i >= 0) {
      noun = visit.noun;
      first = visit.riderStart[i]! + 1;
      form = visit.forms[i]!;
      break;
    }
  }
  // The party went down under saved riders (the review of 20261001110020):
  // the server drops the empty places past the party itself and never a
  // saved rider, so reading the forms again would only refuse again. Say
  // what is wrong instead (P4 asks who is not coming).
  if (form && missingBeyondParty(reason, form.participants, form.party_size, ids))
    return { message: ridersBeyondPartyMessage(noun, form.participants.length, form.party_size), reload: false };
  if (isParticipantIdRefusal(reason)) return { message: PARTICIPANTS_CHANGED_NOTICE, reload: true };
  const named = describeParticipantRefusal(reason, noun, numbersOfIds(draft.rows[bookingId] ?? [], ids, first));
  // identity_changed the page's own check missed (the server's verdict wins):
  // the question opens on the riders it names.
  if (reason === "identity_changed" && ids?.length && named) return { message: named, reload: false, ask: [...ids] };
  return named ? { message: named, reload: false } : null;
}

// ─── "Is this still {name}?", Replace and "Who is not coming?" (P4a) ───────
//
// The rules and words are @vertu/shared's (rider-rename.ts); these apply
// them to the checkout's draft. A rider is named by id, never by place.

/** Every question the list asks, private ones included (a member below manager: the locked list). */
export function allRiderFields(form: CheckoutForm): QuestionField[] {
  return form.locked_fields.length ? form.locked_fields : form.fields;
}

/** The saved rider a draft row names by id (as this page read it), or undefined. */
export function storedRiderOf(form: CheckoutForm, row: unknown): AnswerRow | undefined {
  const id = participantIdOf(row) ?? replacedIdOf(row);
  return id ? form.participants.find((r) => participantIdOf(r) === id) : undefined;
}

/** More riders saved than the party, in the way of every save (notComingOf), for this form. */
export function notComingOfForm(form: CheckoutForm) {
  return notComingOf(allRiderFields(form), form.participants, form.party_size, {
    withheld: form.withheld_rider,
    strict: form.sensitive_access === "staff",
  });
}

/** Does this card change a saved rider's name, on a list that guards it, with no answer yet? */
export function renameNeeded(form: CheckoutForm, draft: CheckoutDraft, ordinal: number): boolean {
  const row = draft.rows[form.booking_id]?.[ordinal];
  if (!row) return false;
  const sent = questionsPayload(form.fields, [typedRow(form.fields, row)])[0] ?? {};
  return renameNeedsAnswer(allRiderFields(form), sent, storedRiderOf(form, row));
}

export type RenameAsk = { bookingId: string; ordinal: number; id: string; who: string; name: string };

/** The cards these visits must ask about before their rows are saved, in card order. */
export function renamesToAsk(visits: readonly CheckoutVisit[], draft: CheckoutDraft, bookingIds?: readonly string[]): RenameAsk[] {
  const out: RenameAsk[] = [];
  for (const visit of visits)
    visit.forms.forEach((form, i) => {
      if (form.fields.length === 0 || (bookingIds && !bookingIds.includes(form.booking_id))) return;
      (draft.rows[form.booking_id] ?? []).forEach((row, ordinal) => {
        const id = participantIdOf(row);
        if (!id || !renameNeeded(form, draft, ordinal)) return;
        out.push({
          bookingId: form.booking_id,
          ordinal,
          id,
          who: `${nounTitle(visit.noun)} ${visit.riderStart[i]! + ordinal + 1}`,
          name: riderNameOf(allRiderFields(form), storedRiderOf(form, row)),
        });
      });
    });
  return out;
}

/**
 * Ask "Is this still {name}?" on these riders (by id). `forced`: the server
 * asked it (identity_changed), so it shows whatever this page's own check
 * says (CheckoutDraft.renameForced).
 */
export function askRename(draft: CheckoutDraft, ids: readonly string[], forced = false): CheckoutDraft {
  const asked = { ...(draft.renameAsked ?? {}) };
  const force = { ...(draft.renameForced ?? {}) };
  for (const raw of ids) {
    const id = raw.toLowerCase();
    asked[id] = asked[id] ?? "ask";
    if (forced) force[id] = true;
  }
  return { ...draft, renameAsked: asked, ...(forced ? { renameForced: force } : {}) };
}

/** Which question this card shows now: null, "ask" or "booker" (see CheckoutDraft.renameAsked). */
export function renameStage(form: CheckoutForm, draft: CheckoutDraft, ordinal: number): "ask" | "booker" | null {
  const id = participantIdOf(draft.rows[form.booking_id]?.[ordinal]);
  const stage = id ? draft.renameAsked?.[id] : undefined;
  if (!stage || !id) return null;
  return draft.renameForced?.[id] || renameNeeded(form, draft, ordinal) ? stage : null;
}

function withRow(draft: CheckoutDraft, bookingId: string, ordinal: number, row: DraftRow): CheckoutDraft {
  const rows = [...(draft.rows[bookingId] ?? [])];
  rows[ordinal] = row;
  return { ...draft, rows: { ...draft.rows, [bookingId]: rows }, dirtyRows: { ...draft.dirtyRows, [bookingId]: true } };
}

function withoutAsked(draft: CheckoutDraft, id: string | null): CheckoutDraft {
  if (!id || !draft.renameAsked || !(id in draft.renameAsked)) return draft;
  const renameAsked = { ...draft.renameAsked };
  delete renameAsked[id];
  const renameForced = draft.renameForced ? { ...draft.renameForced } : undefined;
  if (renameForced) delete renameForced[id];
  return { ...draft, renameAsked, ...(renameForced ? { renameForced } : {}) };
}

/**
 * The answer to "Is this still {name}?" on one card:
 *   same    Yes, fixing a spelling — staff and above only: the row says
 *           "_same_person" and keeps its id and private answers;
 *   booker  Yes, from the booker: not theirs to say (change it back, or
 *           replace this rider);
 *   back    Change it back: the saved name returns, other edits stay;
 *   replace No, someone else: a NEW rider in the same place, only the name
 *           just typed kept, nothing of the saved rider's (the save names
 *           the saved rider it replaces: one save, or a tombstone beside
 *           the new row in a whole-list save);
 *   undo    on a replaced card: the card as it was when Replace was chosen
 *           (the saved name back, other edits kept).
 */
export function answerRename(
  draft: CheckoutDraft,
  form: CheckoutForm,
  ordinal: number,
  answer: "same" | "booker" | "back" | "replace" | "undo",
): CheckoutDraft {
  const row = draft.rows[form.booking_id]?.[ordinal];
  if (!row) return draft;
  const id = participantIdOf(row);
  const stored = storedRiderOf(form, row);
  const fields = allRiderFields(form);
  switch (answer) {
    case "same":
      return withoutAsked(withRow(draft, form.booking_id, ordinal, confirmSamePerson(fields, row)), id);
    case "booker":
      return id ? { ...draft, renameAsked: { ...(draft.renameAsked ?? {}), [id]: "booker" } } : draft;
    case "back":
      return withoutAsked(withRow(draft, form.booking_id, ordinal, changeNameBack(fields, row, stored)), id);
    case "replace": {
      if (!id) return draft;
      const next = withoutAsked(
        withRow(draft, form.booking_id, ordinal, pruneHidden(form.fields, replaceRow(fields, row, id, true, stored) as DraftRow)),
        id,
      );
      const prefix = `r|${form.booking_id}|${ordinal}|`;
      const cleared = next.cleared
        ? (Object.fromEntries(Object.entries(next.cleared).filter(([k]) => !k.startsWith(prefix))) as Record<string, true>)
        : next.cleared;
      return withoutAdopted({ ...next, ...(cleared ? { cleared } : {}) }, form.booking_id, ordinal);
    }
    case "undo": {
      // The card as it was when Replace was chosen (the saved name back,
      // other edits kept), or else the saved rider as this page read it.
      const was = replacedIdOf(row);
      if (!was || !stored) return draft;
      return withRow(draft, form.booking_id, ordinal, undoReplace(row, stored as DraftRow));
    }
  }
}
